import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { Select } from "components/common/Select";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import ListTable from "components/vendorAccess/dashboard/ListTable";
import { VendorDashboardSelector } from "slices/vendor/dashboardSlice";
import { fetchVendorDashboardAnalytics } from "slices/vendor/dashboardSlice";
import { fetchVendorDashboardAppointments } from "slices/vendor/dashboardSlice";
import { LegendButton } from "components/common/TableColor";
import { vendorLegends } from "components/common/TableColor";
import { fetchFacilitiesDirect, fetchFacilities } from "slices/facilitiesSlice";
import { departmentsSelector } from "slices/departmentsSlice";
import { facilitiesSelector } from "slices/facilitiesSlice";
import { fetchDepartmentsDirect, fetchDepartments } from "slices/departmentsSlice";



const DatesInputs = (({ control, errors }) => {
  const startDateRef = React.useRef(null);
  const endDateRef = React.useRef(null);

  return (
    <>
      <div className="row-primary">
        <h2>Start Date*</h2>
        <Controller
          name="start_date"
          control={control}
          render={({ field }) =>
            <div className="input-image">
              <input ref={startDateRef} id='start_date' type="date" name='start_date' placeholder='Start Date'
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(e.target.value).format('YYYY-MM-DD')
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />

            </div>
          } />
      </div>
      <div className="row-primary">
        <h2>End Date*</h2>
        <Controller
          name="end_date"
          control={control}
          render={({ field }) =>
            <div className="input-image ">
              <input ref={endDateRef} type="date" name='end_date' placeholder='End Date'
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(e.target.value).format('YYYY-MM-DD')
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />
            </div>
          } />
      </div>
    </>
  )
});

function Dashboard() {
  const dispatch = useDispatch()
  const { vendorDashboard: dashboard, last_page: totalPages, loader, total, per_page } = useSelector(VendorDashboardSelector);
  const { language: languages } = useSelector(languageSelector);


  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, getValues, setError: setErrors, reset, } = useForm({
    defaultValues: {
      start_date: moment().format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
    }
  });
  const [activeLegend, setActiveLegend] = useState(0);
  const [statusValue, setStatusValue] = React.useState('all');
  const [filterKey, setFilterKey] = React.useState(0);

  const watchedValues = watch(['start_date', 'end_date', 'language_id', 'facility_id', 'department_id', 'service', 'account']);
  const [start_date, end_date, language_id, facility_id, department_id, service, account] = watchedValues;

  const paginationParams = React.useMemo(() => ({
    start_date,
    end_date,
    language_id,
    facility_id,
    department_id,
    service,
    account,
    status: statusValue !== 'all' ? statusValue : undefined
  }), [start_date, end_date, language_id, facility_id, department_id, service, account, statusValue]);


  useEffect(() => {
    dispatch(fetchVendorDashboardAnalytics())
    dispatch(fetchVendorDashboardAppointments(1, { pageLength: 10, start_date: moment().format('YYYY-MM-DD'), end_date: moment().format('YYYY-MM-DD'), sortBy: "id", sortDirection: "desc" }))
    dispatch(fetchFacilitiesDirect())
    dispatch(fetchDepartmentsDirect())
  }, [])



  // #region Language dropdown 
  const filteredLanguages = React.useMemo(() =>
    languages?.map(language => {
      const object = {
        value: language?.id,
        label: language?.name
      }
      return object
    }), [languages]);

  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true))
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion


  // #region Department dropdown
  const [filteredDepartments, setFilteredDepartments] = useState(null);

  const filteredDepartmentsLoadOptions = React.useCallback(async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchDepartments(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result?.data?.map((val) => ({
        value: val.id,
        label: val.department_name || val.name || val.label || val.abbreviation || "",
      })) || [],

      hasMore: page < Number(result?.last_page),
      additional: {
        page: page + 1,
      },
    };
  }, [dispatch]);
  // #endregion


  // #region Facility dropdown
  const [filteredFacilities, setFilteredFacilities] = useState(null);

  const filteredFacilitiesLoadOptions = React.useCallback(async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchFacilities(
        page,
        {
          pageLength: 20,
          sortBy: "name",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result?.data?.map((val) => ({
        value: val.id,
        label: val.facility_name || val.abbreviation || val.name || val.label,
      })) || [],

      hasMore: page < Number(result?.last_page),
      additional: {
        page: page + 1,
      },
    };
  }, [dispatch]);

  // #endregion



  // #region Legends 
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...vendorLegends];
    temp_array.unshift({
      value: "all",
      name: "All",
      id: 0,
      color: "#3176A0"
    })
    return temp_array;
  }, [vendorLegends]);

  const hanldeActiveLegend = (id, value) => {
    setActiveLegend(id);
    const formValues = getValues();
    dispatch(fetchVendorDashboardAppointments(1, {
      ...formValues,
      pageLength: per_page || 10,
      status: value,
      start_date: moment(formValues.start_date || watch('start_date')).format('YYYY-MM-DD'),
      end_date: moment(formValues.end_date || watch('end_date')).format('YYYY-MM-DD'),
      sortBy: "id",
      sortDirection: "desc"
    }))
    setStatusValue(value)
    setFilterKey(prev => prev + 1)
  }

  //#endregion


  const onSubmit = (data) => {
    dispatch(setError({}))
    dispatch(fetchVendorDashboardAppointments(1, {
      ...data,
      pageLength: per_page || 10,
      status: statusValue !== 'all' ? statusValue : undefined
    }))
    clearErrors();
    setFilterKey(prev => prev + 1)
  };


  const handleResetFields = () => {
    const today = moment().format('YYYY-MM-DD');
    setValue("start_date", today);
    setValue("end_date", today);
    setValue("language_id", "");
    setValue("account", "");
    setValue("department_id", "");
    setValue("facility_id", "");
    setFilteredFacilities(null);
    setFilteredDepartments(null);
    setValue("service", "");
    setActiveLegend(0);
    setStatusValue('all');
    dispatch(fetchVendorDashboardAppointments(1, { start_date: today, end_date: today, sortBy: "id", sortDirection: "desc" }))
    setFilterKey(prev => prev + 1)
    clearErrors()
  };


  const serviceOption = [
    { value: 'In Person', label: 'In Person' },
    { value: 'VRI', label: 'Video Remote Interpretation' },
    { value: 'OPI', label: 'OTP Pre Schedule' },
    { value: 'OPI On Demand', label: 'OPI On Demand' },
  ]

  const [isFourteenDays, setIsFourteenDays] = useState(false);
  const tableRef = useRef(null);

  const handleFilter = (item, isforteen, statusFilter = 'all') => {
    const today = moment().format('YYYY-MM-DD');
    const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD');
    const next_fourteen_days = moment().add(14, 'days').format('YYYY-MM-DD');

    if (isforteen) {
      setValue("start_date", tomorrow);
      setValue("end_date", next_fourteen_days);
    } else {
      setValue("start_date", today);
      setValue("end_date", today);
    }

    setValue("service", item?.value || null);

    // Sync the legend button
    const matchedLegend = filteredStatuses.find(s => s.value === statusFilter);
    setActiveLegend(matchedLegend?.id ?? 0);
    setStatusValue(statusFilter);

    const formValues = getValues();
    const data = {
      ...formValues,
      pageLength: per_page || 10,
      start_date: isforteen ? tomorrow : today,
      end_date: isforteen ? next_fourteen_days : today,
      service: item?.value || "",
      status: statusFilter !== 'all' ? statusFilter : undefined,
    };

    dispatch(fetchVendorDashboardAppointments(1, data));
    tableRef.current?.scrollIntoView({ behavior: "smooth" });
    dispatch(setError({}));
    clearErrors();
  };

  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Dashboard</h2>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Today</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr key={index}>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'all'); }}>{service?.type}</td>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'registered'); }}>{service?.count}</td>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'open'); }}>{service?.to_cover}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Next 14 Days</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Next 14 Days</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map((service, index) => (
                    <tr key={index}>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'all'); }}>{service?.type}</td>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'registered'); }}>{service?.count}</td>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'open'); }}>{service?.to_cover}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* <ScheduleModal /> */}
        <div className="card_table">
          <div className="button-sm forLegends p-3">
            <LegendButton activeId={activeLegend} options={filteredStatuses} handleClick={hanldeActiveLegend} />
          </div>
          <form className="search_form_ad">
            <div className="row-primary dahboard_filter">
              <DatesInputs control={control} errors={errors} />
              {/* <div className="row-primary">
                <h2>Clients</h2>
                <Controller
                  name="account"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis" style={{ width: "100%" }}>
                      <input className="input-in type_inp" name="account" type="text" placeholder="Client name" {...field} />
                    </div>
                  }
                />
              </div> */}
              <div className="row-primary">
                <h2>Facility</h2>
                <Controller
                  name="facility_id"
                  control={control}
                  render={({ field }) =>
                    <div className="w-100">
                      <div className="w-100">
                        <AsyncPaginate
                          menuPlacement='auto'
                          styles={{
                            ...selectCustomStyles,
                          }}
                          debounceTimeout={300}
                          value={filteredFacilities}
                          loadOptions={filteredFacilitiesLoadOptions}
                          isClearable
                          onChange={(obj) => {
                            setFilteredFacilities(obj);
                            field.onChange(obj?.value);
                          }}
                          additional={{
                            page: 1,
                          }}
                        />
                      </div>
                    </div>
                  }
                />
              </div>

              <div className="row-primary">
                <h2>Language</h2>
                <Controller
                  name="language_id"
                  control={control}
                  render={({ field }) =>
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={field.value ? filteredLanguages?.find((opt) => field.value == opt.value) : ""}
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  }
                />
              </div>
              {/* <div className="row-primary">
                <h2>Services</h2>

                <Controller
                  name="service"
                  control={control}
                  render={({ field }) =>
                    <div style={{ width: "100%" }}>
                      <Select
                        name="service"
                        options={serviceOption || []}
                        {...field}
                        value={field.value ? serviceOption?.find((opt) => field.value == opt.value) : ""}
                      />
                    </div>
                  }
                />
              </div> */}
              <div className="row-primary">
                <h2>Department</h2>
                <Controller
                  name="department_id"
                  control={control}
                  render={({ field }) =>
                    <div className="w-100">
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{
                          ...selectCustomStyles,
                        }}
                        debounceTimeout={300}
                        value={filteredDepartments}
                        loadOptions={filteredDepartmentsLoadOptions}
                        isClearable
                        onChange={(obj) => {
                          setFilteredDepartments(obj);
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  }
                />
              </div>

            </div>

            <div className="row-primary btns_area row_10">
              <SubmitButton handleSubmit={handleSubmit(onSubmit)} buttonType="button" btnText="Search" className={'green'} />
              <SubmitButton buttonType="button" className={'lightBlue'} btnText="Reset" handleSubmit={handleResetFields} />
            </div>

          </form>

          <div ref={tableRef}>
            <ListTable data={dashboard?.appointments} paginationParams={paginationParams} filterKey={filterKey} />
          </div>
        </div>
      </div>

      {loader && <FullPageLoader />}
    </>
  );
}

export default Dashboard;